import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { getComponentPrevalenceLookups } from "@-/dev.improve.haniverse.data.component-prevalence";
import {
  EMPTY_TAIWAN_TBCL_SET,
  EMPTY_TAIWAN_USAGE_MAP,
  getTaiwanTbclSet,
  getTaiwanUsageMap,
} from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import type { ContentDatabase } from "@-/dev.improve.haniverse.database.content";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import {
  type PinyinSoundIntroductions,
  findPinyinSoundIntroductions,
  listReadingLessons,
} from "@-/dev.improve.haniverse.lib.level";
import { getGraphemePrevalence } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.prevalence";

function readPinyinSoundIntroductions(
  contentDb: ContentDatabase,
  hanziScript: HanziScript,
): Promise<PinyinSoundIntroductions> {
  const isTraditional = hanziScript === "traditional";
  // Enrollment gives a grapheme with no prevalent reading (strokes, radicals) no pronunciation card.
  const { simplified, traditional } = getComponentPrevalenceLookups();
  const prevalenceOptions = {
    simplifiedComponentPrevalenceLookup: simplified,
    twComponentPrevalenceLookup: traditional,
    twTbclSet: isTraditional ? getTaiwanTbclSet() : EMPTY_TAIWAN_TBCL_SET,
    twUsageMap: isTraditional ? getTaiwanUsageMap() : EMPTY_TAIWAN_USAGE_MAP,
  };
  return findPinyinSoundIntroductions({
    hasPronunciationCards: (grapheme) =>
      getGraphemePrevalence(grapheme, hanziScript, prevalenceOptions).asCharacter !== "obscure",
    lessons: listReadingLessons(isTraditional ? getTraditionalLevels() : getSimplifiedLevels()),
    readReadings: (graphemes) =>
      contentDb(import.meta.location).query.appGraphemePronunciationCard.findMany({
        columns: {
          grapheme: true,
          hsk2026Level: true,
          isMain: true,
          pinyin: true,
          tbclLevel: true,
        },
        where: { grapheme: { in: [...graphemes] } },
      }),
    script: hanziScript,
  });
}

/**
 * The first reading the `hanziScript` curriculum introduces with each pinyin initial and final.
 * The bundled levels and the content database never change while the app runs, so the result is
 * read once and kept.
 */
export function usePinyinSoundIntroductionsQuery(hanziScript: HanziScript) {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (context) => context.db);
  return useQuery({
    gcTime: Number.POSITIVE_INFINITY,
    queryFn: () => readPinyinSoundIntroductions(contentDb, hanziScript),
    queryKey: ["pinyinSoundIntroductions", hanziScript, { contentDb }] as const,
    staleTime: Number.POSITIVE_INFINITY,
  });
}
